import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LandingNavbar from "../landing/LandingNavbar";
import Footer from "../landing/Footer";
import { Logo, LoadingSpinner } from "../../design-system";
import { UserProfileIcon, CalendarIcon, ChevronRightIcon } from "../../design-system/icons";
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";
import { loadAllArticles } from "./utils/articleLoader";
import { APP_DOMAIN } from '../../core/utils/config';

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const loadedArticles = await loadAllArticles();
        setArticles(loadedArticles);
      } catch (err) {
        setError(err.message);
        console.error('Error loading articles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
        <LandingNavbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <LoadingSpinner size="xl" color="primary" />
            </div>
            <p className={textColors.secondary}>Loading articles...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
        <LandingNavbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">Error loading articles: {error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className={`${brandColors.primaryBg} ${brandColors.primaryBgHover} text-white px-4 py-2 rounded-md transition-colors`}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
      <LandingNavbar />
      
      <div className="flex-1 pt-16">
        {/* Brand Gradient Header Section */}
        <div className={`bg-gradient-to-r ${brandColors.gradientFrom} ${brandColors.gradientTo} text-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className={`${typography.h1Dark} mb-4 flex items-center justify-center space-x-3`}>
              <span>{APP_DOMAIN} Blog</span>
            </h1>
            <p className={typography.bodyLargeDark}>
              Insights, tips, and stories about portfolio creation and personal branding
            </p>
          </div>
          </div>
        </div>

        {/* Articles List */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {articles.length === 0 ? (
            <div className="text-center py-12">
              <p className={`${typography.bodyLarge} ${textColors.primary} mb-2`}>No articles available yet.</p>
              <p className={`${typography.body} ${textColors.secondary}`}>Check back soon for new content!</p>
            </div>
          ) : (
            <div className="space-y-8">
              {articles.map((article, index) => (
                <article 
                  key={article.slug} 
                  className={`${surfaces.primary} rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300`}
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Link 
                          to={`/blog/${article.slug}`}
                          className="block group"
                        >
                          <h2 className={`${typography.h3} ${textColors.primary} mb-3 ${brandColors.primaryHover} transition-colors duration-200`}>
                            {article.title}
                          </h2>
                        </Link>
                        
                        <div className={`${typography.bodySmall} ${textColors.secondary} mb-4 space-x-4 flex items-center`}>
                          <div className="flex items-center">
                            <UserProfileIcon size="xs" className="mr-2" />
                            <span className="font-medium">{article.author}</span>
                          </div>
                          
                          <div className="flex items-center">
                            <CalendarIcon size="xs" className="mr-2" />
                            <time>{formatDate(article.date)}</time>
                          </div>
                        </div>

                        {/* Article preview - first paragraph */}
                        <div className={`${typography.body} ${textColors.secondary} mb-4`}>
                          {article.content.split('\n\n')[0].replace(/^#+ /, '').substring(0, 200)}
                          {article.content.split('\n\n')[0].length > 200 && '...'}
                        </div>

                        <Link 
                          to={`/blog/${article.slug}`}
                          className={`inline-flex items-center ${typography.link}`}
                        >
                          Read more
                          <ChevronRightIcon size="xs" className="ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  {/* Optional: Add a subtle border between articles */}
                  {index < articles.length - 1 && (
                    <div className={`border-b border-gray-100 dark:border-gray-700`}></div>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Blog;